import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getChildActivities, getParentChildActivities, type ChildActivity } from '../../services/parentService';
import { useMemo } from 'react';

// Hook for today's activities
export const useTodayActivities = (childId: string | undefined) => {
  const today = new Date().toISOString().split('T')[0];

  return useQuery({
    queryKey: ['parent', 'activities', childId, 'today', today],
    queryFn: () => getChildActivities(childId!, { date: today }),
    enabled: !!childId,
    staleTime: 1 * 60 * 1000, // 1 minute - refresh frequently for live updates
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for near real-time
  });
};

// Hook for paginated activities with date range
export const useChildActivitiesInfinite = (
  childId: string | undefined,
  startDate?: string,
  endDate?: string
) => {
  return useInfiniteQuery({
    queryKey: ['parent', 'activities', childId, 'infinite', startDate, endDate],
    queryFn: ({ pageParam = 1 }) =>
      getParentChildActivities(childId!, {
        page: pageParam,
        per_page: 20,
        start_date: startDate,
        end_date: endDate,
      }),
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.pagination;
      if (pagination && pagination.current_page < pagination.total_pages) {
        return pagination.current_page + 1;
      }
      return undefined;
    },
    enabled: !!childId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook to get activities grouped by time
export const useActivitiesByTime = (childId: string | undefined, date?: string) => {
  const { data, ...rest } = useQuery({
    queryKey: ['parent', 'activities', childId, date],
    queryFn: () => getChildActivities(childId!, { date }),
    enabled: !!childId,
  });

  const groupedActivities = useMemo(() => {
    const activities = Array.isArray(data) ? data : (data?.items || []);
    if (!activities.length) return {};

    return activities.reduce((groups: Record<string, ChildActivity[]>, activity: ChildActivity) => {
      const timestamp = activity.timestamp || activity.detected_at || new Date().toISOString();
      const hour = new Date(timestamp).getHours();
      const timeSlot = `${hour}:00`;

      if (!groups[timeSlot]) {
        groups[timeSlot] = [];
      }
      groups[timeSlot].push(activity);

      return groups;
    }, {} as Record<string, ChildActivity[]>);
  }, [data]);

  return {
    ...rest,
    data,
    groupedActivities,
  };
};
